import type { SxProps, Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";

export const layout: SxProps<Theme> = (theme) => ({
  display: "grid",
  gridTemplateColumns: { xs: "1fr", lg: "1fr 400px" },
  gap: theme.spacing(2.5),
  alignItems: "start",
});

export const panelCard: SxProps<Theme> = (theme) => ({
  bgcolor: "background.paper",
  borderRadius: 2.75,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[3],
  p: { xs: 2, md: 3 },
  display: "flex",
  flexDirection: "column",
  gap: 2.25,
  minWidth: 0,
});

export const cartCard: SxProps<Theme> = (theme) => ({
  bgcolor: "background.paper",
  borderRadius: 2.75,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[3],
  p: { xs: 2, md: 2.75 },
  display: "flex",
  flexDirection: "column",
  gap: 2,
  minWidth: 0,
  position: { lg: "sticky" },
  top: { lg: 84 },
});

export const sectionHeader: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1,
};

export const sectionTitle: SxProps<Theme> = {
  fontWeight: 700,
  fontSize: "1rem",
};

export const cartBadge: SxProps<Theme> = (theme) => ({
  fontSize: "0.71875rem",
  fontWeight: 700,
  color: "primary.main",
  bgcolor: alpha(theme.palette.primary.main, 0.08),
  px: 1.25,
  py: 0.5,
  borderRadius: 2.5,
});

export const categoryRow: SxProps<Theme> = {
  display: "flex",
  flexWrap: "wrap",
  gap: 1,
};

export const productList: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  maxHeight: { md: 460 },
  overflowY: "auto",
};

export const productRow =
  (dimmed: boolean): SxProps<Theme> =>
  (theme) => ({
    display: "flex",
    alignItems: "center",
    gap: 1.75,
    py: 1.625,
    px: 0.5,
    borderTop: `1px solid ${theme.palette.divider}`,
    opacity: dimmed ? 0.55 : 1,
  });

export const productInfo: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 0.375,
};

export const productName: SxProps<Theme> = {
  fontSize: "0.875rem",
  fontWeight: 600,
};

export const productMeta: SxProps<Theme> = {
  fontSize: "0.75rem",
  color: "text.secondary",
};

export const emptyPicker: SxProps<Theme> = {
  py: 5.5,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 1.25,
  textAlign: "center",
};

export const emptyCart: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  gap: 1,
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: 1.75,
  p: 3.25,
});

export const cartList: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  maxHeight: 300,
  overflowY: "auto",
};

export const cartItem: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: 1.125,
  py: 1.5,
  px: 0.25,
  borderTop: `1px solid ${theme.palette.divider}`,
});

export const cartItemTop: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1.25,
};

export const cartItemInfo: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
};

export const cartItemName: SxProps<Theme> = {
  fontSize: "0.8125rem",
  fontWeight: 600,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export const cartItemUnit: SxProps<Theme> = {
  fontSize: "0.6875rem",
  color: "text.secondary",
};

export const cartItemTotal: SxProps<Theme> = {
  fontSize: "0.8125rem",
  fontWeight: 700,
  flexShrink: 0,
};

export const cartItemBottom: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
};

export const removeButton: SxProps<Theme> = {
  width: 30,
  height: 30,
  borderRadius: 1.125,
  border: "none",
  bgcolor: "transparent",
  color: "text.disabled",
  p: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  flexShrink: 0,
  transition: "color 0.2s ease",
  "&:hover": {
    color: "error.main",
  },
};

export const summaryBlock: SxProps<Theme> = (theme) => ({
  borderTop: `1px dashed ${theme.palette.divider}`,
  pt: 1.75,
  display: "flex",
  flexDirection: "column",
  gap: 1,
});

export const summaryRow: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

export const summaryLabel: SxProps<Theme> = {
  fontSize: "0.8125rem",
  color: "text.secondary",
};

export const summaryValue: SxProps<Theme> = {
  fontSize: "0.8125rem",
  fontWeight: 600,
};

export const totalValue: SxProps<Theme> = {
  fontSize: "1.0625rem",
  fontWeight: 800,
};

export const totalCurrency: SxProps<Theme> = {
  fontSize: "0.6875rem",
  fontWeight: 500,
  color: "text.secondary",
};

export const successBanner: SxProps<Theme> = (theme) => ({
  display: "flex",
  alignItems: "center",
  gap: 1,
  bgcolor: alpha(theme.palette.success.main, 0.08),
  borderRadius: 1.5,
  p: theme.spacing(1.375, 1.625),
});

export const successText: SxProps<Theme> = {
  fontSize: "0.78125rem",
  fontWeight: 700,
  color: "success.main",
};

export const linkHint: SxProps<Theme> = {
  fontSize: "0.71875rem",
  color: "text.secondary",
  lineHeight: 1.8,
  m: 0,
};

export const ghostActions: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1,
};

export const ghostButton: SxProps<Theme> = (theme) => ({
  flex: 1,
  height: 40,
  borderRadius: 1.375,
  border: `1px solid ${theme.palette.divider}`,
  bgcolor: "background.paper",
  fontFamily: "inherit",
  fontWeight: 700,
  fontSize: "0.78125rem",
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    borderColor: alpha(theme.palette.primary.main, 0.3),
    color: "primary.main",
  },
});

export const ctaHint: SxProps<Theme> = {
  textAlign: "center",
  fontSize: "0.71875rem",
  color: "text.disabled",
  lineHeight: 1.7,
  m: 0,
};

export const loadingWrap: SxProps<Theme> = {
  display: "flex",
  justifyContent: "center",
  py: 5,
};

export const mobileStickyCta: SxProps<Theme> = (theme) => ({
  display: { xs: "flex", lg: "none" },
  position: "fixed",
  insetInline: 0,
  bottom: 0,
  bgcolor: "background.paper",
  borderTop: `1px solid ${theme.palette.divider}`,
  p: theme.spacing(1.75, 2.25, 2.25),
  flexDirection: "column",
  gap: 1,
  zIndex: theme.zIndex.appBar,
});

export const pageBottomSpacer: SxProps<Theme> = {
  display: { xs: "block", lg: "none" },
  height: 88,
};

export const addButton: SxProps<Theme> = (theme) => ({
  width: 40,
  height: 40,
  borderRadius: 1.5,
  border: "none",
  bgcolor: "primary.main",
  color: "primary.contrastText",
  display: { xs: "flex", md: "none" },
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  flexShrink: 0,
  boxShadow: `0 6px 14px ${alpha(theme.palette.primary.main, 0.28)}`,
});

export const ctaError: SxProps<Theme> = {
  textAlign: "center",
  fontSize: "0.75rem",
  fontWeight: 600,
  color: "error.main",
  lineHeight: 1.7,
  m: 0,
};
